import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-[length:var(--font-size-product-ui)] font-normal transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none forced-colors:border forced-colors:border-[ButtonBorder] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-[var(--od-button-primary-hover)]",
        primary:
          "bg-primary text-primary-foreground shadow hover:bg-[var(--od-button-primary-hover)]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        outlineLight:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-accent-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-8 px-3 py-1.5",
        sm: "h-7 rounded-md px-2.5",
        lg: "h-9 rounded-md px-6",
        icon: "h-8 w-8",
        touch: "min-h-11 px-4 py-2",
        iconTouch: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonEmphasis = "solid" | "outline" | "ghost" | "link";
export type ButtonIntent =
  | "brand"
  | "neutral"
  | "success"
  | "warning"
  | "info"
  | "danger";

const buttonPresentationClasses: Record<
  ButtonEmphasis,
  Record<ButtonIntent, string>
> = {
  solid: {
    brand:
      "bg-primary text-primary-foreground shadow hover:bg-[var(--od-button-primary-hover)]",
    neutral:
      "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
    success:
      "bg-[var(--success)] text-[var(--success-foreground)] shadow-sm hover:opacity-90",
    warning:
      "bg-[var(--warning)] text-[var(--warning-foreground)] shadow-sm hover:opacity-90",
    info: "bg-[var(--info)] text-[var(--info-foreground)] shadow-sm hover:opacity-90",
    danger:
      "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
  },
  outline: {
    brand:
      "border border-primary bg-background text-accent-foreground shadow-sm hover:bg-accent",
    neutral:
      "border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
    success:
      "border border-[var(--success)] bg-background text-success shadow-sm hover:bg-success",
    warning:
      "border border-[var(--warning)] bg-background text-warning shadow-sm hover:bg-warning",
    info: "border border-[var(--info)] bg-background text-info shadow-sm hover:bg-info",
    danger:
      "border border-destructive bg-background text-destructive shadow-sm hover:bg-destructive/10",
  },
  ghost: {
    brand:
      "text-accent-foreground hover:bg-accent hover:text-accent-foreground",
    neutral: "text-foreground hover:bg-accent hover:text-accent-foreground",
    success: "text-success hover:bg-success",
    warning: "text-warning hover:bg-warning",
    info: "text-info hover:bg-info",
    danger: "text-destructive hover:bg-destructive/10",
  },
  link: {
    brand: "h-auto text-accent-foreground underline-offset-4 hover:underline",
    neutral: "h-auto text-foreground underline-offset-4 hover:underline",
    success: "h-auto text-success underline-offset-4 hover:underline",
    warning: "h-auto text-warning underline-offset-4 hover:underline",
    info: "h-auto text-info underline-offset-4 hover:underline",
    danger: "h-auto text-destructive underline-offset-4 hover:underline",
  },
};

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  busy?: boolean;
  emphasis?: ButtonEmphasis;
  intent?: ButtonIntent;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      busy,
      emphasis,
      intent,
      isLoading = false,
      children,
      disabled,
      onClick,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    const usesSemanticAxes = emphasis !== undefined || intent !== undefined;
    const usesBusyApi = busy !== undefined;
    const isBusy = Boolean(busy || isLoading);
    const usesLegacyLoading = Boolean(
      isLoading && !usesSemanticAxes && !usesBusyApi,
    );
    const needsContentWrapper = usesSemanticAxes || usesBusyApi;
    const presentationClasses = usesSemanticAxes
      ? buttonPresentationClasses[emphasis ?? "solid"][intent ?? "brand"]
      : undefined;

    const content = needsContentWrapper ? (
      <span
        className={cn(
          "inline-flex max-w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1",
          isBusy && "opacity-0",
        )}
      >
        {children}
      </span>
    ) : (
      children
    );

    return (
      <button
        className={cn(
          buttonVariants({
            variant: usesSemanticAxes ? null : variant,
            size,
          }),
          presentationClasses,
          isBusy && !usesLegacyLoading && "relative",
          usesSemanticAxes &&
            "max-w-full text-center font-medium whitespace-normal active:translate-y-px motion-reduce:active:translate-y-0",
          className,
        )}
        ref={ref}
        disabled={disabled || isBusy}
        onClick={onClick}
        aria-busy={isBusy || undefined}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        {...props}
      >
        {usesLegacyLoading && (
          <svg
            aria-hidden="true"
            className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {content}
        {isBusy && !usesLegacyLoading && (
          <svg
            aria-hidden="true"
            className="absolute h-4 w-4 animate-spin motion-reduce:animate-none"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

export interface IconButtonProps extends Omit<ButtonProps, "size"> {
  "aria-label": string;
  size?: "icon" | "iconTouch";
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = "iconTouch", onClick, ...props }, ref) => (
    <Button ref={ref} size={size} onClick={onClick} {...props} />
  ),
);
IconButton.displayName = "IconButton";

export { Button, IconButton, buttonVariants };
